import type { EmrCodeItem, ProcedureItem } from 'features/Procedure/state/procedure-api.type'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'
import { LASER_KEYWORD, type FormValues } from './schema'

export enum ProcedureTab {
  CPT = 'cpt',
  ICD = 'icd',
}

export const EMPTY_PROCEDURE_CODE = 999999

export const defaultInitialValues: FormValues = {
  activeTab: ProcedureTab.CPT,
  isLaser: false,
  cpt4_code: null,
  icd10_codes: [],
  procedure_units: undefined,
  units: 1,
  price: 0,
  area: undefined,
  energy: undefined,
  pulse_per_section: undefined,
  total_energy: undefined,
  discount_type_id: null,
}

const parseJustifyCodes = (justifyCodes: string[]): EmrCodeItem[] => {
  return justifyCodes
    .filter((code) => code.startsWith(ProcedureTypeCode.ICD10))
    .map((code) => {
      const [, icd10Code] = code.split('|')
      return {
        code: icd10Code.replace(/\s+/g, ''),
        code_type: ProcedureTypeCode.ICD10,
        code_text: '',
        code_text_short: '',
        active: 1,
        id: 0,
      }
    })
}

export const formatJustifyCodes = (icd10Codes: EmrCodeItem[]): string[] => {
  return icd10Codes.map((icd10) => `${icd10.code_type}|${icd10.code.replace(/\s+/g, '')}`)
}

export const getInitialValues = (
  procedure?: ProcedureItem | null,
  defaultDiscountTypeId?: string | null,
): FormValues => {
  if (!procedure) {
    return {
      ...defaultInitialValues,
      discount_type_id: defaultDiscountTypeId || null,
    }
  }

  const isCptProcedure = procedure.code_type === ProcedureTypeCode.CPT4

  if (!isCptProcedure) {
    return {
      ...defaultInitialValues,
      activeTab: ProcedureTab.ICD,
      icd10_codes: parseJustifyCodes(procedure.justify_codes || []),
      discount_type_id: procedure.discount_type || defaultDiscountTypeId || null,
    }
  }

  const cpt4CodeItem: EmrCodeItem = {
    code: procedure.code,
    code_type: procedure.code_type,
    code_text: procedure.code_text || '',
    code_text_short: '',
    active: 1,
    id: procedure.id,
  }

  const icd10CodeItems = parseJustifyCodes(procedure.justify_codes || [])

  const isLaser =
    cpt4CodeItem.code_text?.toLowerCase().includes(LASER_KEYWORD) ||
    cpt4CodeItem.code_text_short?.toLowerCase().includes(LASER_KEYWORD) ||
    false

  return {
    activeTab: ProcedureTab.CPT,
    isLaser,
    cpt4_code: cpt4CodeItem,
    icd10_codes: icd10CodeItems,
    procedure_units: procedure.procedure_units || undefined,
    units: procedure.units,
    price: procedure.fee,
    area: procedure.area || undefined,
    energy: procedure.energy || undefined,
    pulse_per_section: procedure.pulse_per_section || undefined,
    total_energy: procedure.total_energy || undefined,
    discount_type_id: procedure.discount_type || defaultDiscountTypeId || null,
  }
}
